import React, { useEffect, useReducer } from 'react';
import { PageHeader, Tabs, Tab, Panel, SearchBar, formatDate, Pager, Button } from '@keplerco/core';
import { useAppActions, useAppState } from '../../../overmind';
import { useParams } from 'react-router';
import { PagePath } from '../../../navigation/navigation.enums';
import { FetchStatus, FetchType } from '../../../enums';
import { PageLoaderLayer } from '../../../components/general/loading-state/loader-layers/page-loader-layer/page-loader-layer';
import { EmptyState } from '../../../components/general/empty-state/empty-state';
import { AgentResponseDto } from '../../../overmind/actions/ai/ai-agent-responses';
import * as store from './information-search.store';
import { WidgetCard } from '../../analytics/analytics-filters/widget-card';
import { JsonViewer } from '../../../components/json-viewer/json-viewer';
import { formatJson } from '../../../library/helpers/format-json';
import classNames from 'classnames';
import { AgentResponsesSearchRequest } from './information-search.models';
import { InformationSearchDetailsPanel } from './information-search-details.panel';

export function InformationSearchPage(): JSX.Element {
  const params = useParams();

  const actions = useAppActions();
  const { fetchState, companyVariables } = useAppState();

  const [state, dispatch] = useReducer(store.reducer, store.initialState);

  async function getData(request: AgentResponsesSearchRequest) {
    let agentResponses: AgentResponseDto[] = [];
    let totalCount = 0;
    let totalPages = 1;

    const searchResults = await actions.searchAgentResponses({
      companySlug: request.companySlug,
      searchParams: {
        query: request.search,
        agentTypeName: request.agentTypeName,
        maxResults: request.pageSize,
      },
    });

    agentResponses = searchResults ?? [];
    totalCount = searchResults?.length ?? 0;
    totalPages = !!totalCount ? Math.ceil(totalCount / request.pageSize) : 0;

    dispatch({
      type: store.AgentResponsesActionTypes.SetData,
      payload: {
        agentResponses,
        totalCount,
        totalPages,
      },
    });

    actions.stopLoader(PagePath.aiManagementInformationSearch);
  }

  useEffect(() => {
    async function initPage() {
      actions.startLoader({ path: PagePath.aiManagementInformationSearch, type: FetchType.PageFetching });

      const agentTypes = await actions.getAgentTypes();
      dispatch({ type: store.AgentResponsesActionTypes.SetAgentTypes, payload: agentTypes ?? [] });

      const request: AgentResponsesSearchRequest = {
        ...state.request,
        companySlug: params.companySlug || '',
        page: 1,
      };
      dispatch({ type: store.AgentResponsesActionTypes.SetRequest, payload: request });
    }

    initPage();
  }, [companyVariables.slug]);

  useEffect(() => {
    if (!state.request.companySlug) return;
    getData(state.request);
  }, [state.request]);

  function handleTabClick(agentTypeName: string | undefined) {
    actions.startLoader({ path: PagePath.aiManagementInformationSearch, type: FetchType.PageFetching });
    dispatch({
      type: store.AgentResponsesActionTypes.SetRequest,
      payload: {
        ...state.request,
        agentTypeName,
        page: 1,
      },
    });
  }

  function renderTabs(): React.ReactElement {
    const tabs = [<Tab key="all" id="all" name="All" active={state.request.agentTypeName === undefined} onClick={() => handleTabClick(undefined)} />];
    if (!!state.agentTypes) tabs.push(...state.agentTypes.map(agentType => <Tab key={agentType.name} id={agentType.name} name={agentType.displayName} active={state.request.agentTypeName === agentType.name} onClick={() => handleTabClick(agentType.name)} />));

    return <Tabs static>{tabs}</Tabs>;
  }

  return (
    <React.Fragment>
      <PageLoaderLayer path={PagePath.aiManagementInformationSearch}>
        <div className="wrapper stack" style={{ alignItems: 'flex-start', gap: 24 }}>
          <PageHeader
            breadcrumbs={[
              { name: 'AI Management', url: PagePath.aiManagementBase },
              { name: 'Information Search', url: location.pathname },
            ]}
            title="Information Search"
          />

          {renderTabs()}

          <SearchBar
            label="Search"
            loading={fetchState[PagePath.aiManagementInformationSearch]?.status === FetchStatus.Active && fetchState[PagePath.aiManagementInformationSearch]?.type === FetchType.Custom}
            onInput={event => {
              actions.startLoader({ path: PagePath.aiManagementInformationSearch, type: FetchType.PageFetching });
              dispatch({
                type: store.AgentResponsesActionTypes.SetRequest,
                payload: {
                  ...state.request,
                  search: (event as React.ChangeEvent<HTMLInputElement>).target.value,
                  page: 1,
                },
              });
            }}
          />

          {state.data?.agentResponses && state.data.agentResponses.length > 0 ? (
            <React.Fragment>
              {state.data.agentResponses.map(response => (
                <WidgetCard
                  key={response.id}
                  title={response.displayName || response.agentTypeName}
                  subtitle={formatDate(new Date(response.dateCreated))}
                  button={
                    <Button
                      type="button"
                      onClick={async () => {
                        dispatch({ type: store.AgentResponsesActionTypes.SetLoadingAgentDetails, payload: true });

                        const agentDetails = await actions.getAgentResponseById({
                          companySlug: state.request.companySlug,
                          id: response.id,
                        });
                        dispatch({ type: store.AgentResponsesActionTypes.SetAgentDetails, payload: agentDetails });

                        dispatch({ type: store.AgentResponsesActionTypes.SetLoadingAgentDetails, payload: false });
                      }}
                      isLoading={state.loadingAgentDetails}
                      loaderText="Loading..."
                    >
                      View Details
                    </Button>
                  }
                >
                  <JsonViewer data={formatJson(response.rawJsonResponse)} isPreview={true} hideRawToggle={true} recordCount={response.recordCount} enableTextHighlighting={true} />
                </WidgetCard>
              ))}

              <div className={classNames({ invisible: state.data.totalPages < 2 })}>
                <Pager
                  activePageNumber={state.request.page}
                  pageCount={state.data.totalPages}
                  onPageChange={page => {
                    if (page === state.request?.page) return;

                    actions.startLoader({ path: PagePath.aiManagementInformationSearch, type: FetchType.PageFetching });
                    dispatch({
                      type: store.AgentResponsesActionTypes.SetRequest,
                      payload: { ...state.request, page },
                    });
                  }}
                />
              </div>
            </React.Fragment>
          ) : (
            <EmptyState title="No Agent Responses Found" subtitle={state.request.search ? `No agent responses match your search criteria. Try adjusting your search.` : `No agent responses available for the selected type.`} />
          )}
        </div>
      </PageLoaderLayer>

      <Panel open={!!state.agentDetails} onClose={() => dispatch({ type: store.AgentResponsesActionTypes.SetAgentDetails, payload: undefined })}>
        {!!state.agentDetails && <InformationSearchDetailsPanel response={state.agentDetails} />}
      </Panel>
    </React.Fragment>
  );
}
